/** @prettier */

import {useEffect, useState} from 'react';
import * as apiIF from '../../Type/api';
import * as type from '../../Type/TypeDef';
import * as mySiteLib from '../../routes/my-site/mySiteLibrary';
import {format} from 'date-fns';
import {utcToZonedTime, zonedTimeToUtc} from 'date-fns-tz';
import {getStatesSites} from '../../API/API_Collection';

/**
 * サイトに所属する機器(空調機、給湯器、冷凍機)の消費電力を取得するフック
 * @param siteProfile {apiIF.resParameters.siteInformations[]} サイトの情報
 *
 * @return viewSitePwData 機器毎の消費電力
 */
export const useCulcPowAllUnit = (
  siteProfile: apiIF.resParameters.siteInformations[] | null
) => {
  /**サイト消費電力情報*/
  const [viewSitePwData, setViewSitePwData] = useState<
    type.mysiteProps.siteKWData[]
  >([]);

  //消費電力取得用
  //１日分のデータ処理はAPIの応答に時間がかかるので、消費電力量だけ分離する
  useEffect(() => {
    if (siteProfile === null) return;

    /**
     * サイトのユニット種別消費電力量
     * @param statesSites {apiIF.res.getStatesSites['site-states'][]} サイト情報
     * @return {type.mysiteProps.siteKWData[]} サイトユニット別消費電力量
     */
    function calcPwStatesData(
      statesSites: apiIF.res.getStatesSites['site-states']
    ): type.mysiteProps.siteKWData[] {
      /**
       * API戻り値の消費電力量WをKW変換(小数点以下四捨五入)
       */
      const convNumKw = mySiteLib.convNumKw;

      /**サイト消費電力量*/
      let sitePwData: type.mysiteProps.siteKWData[] = [];

      statesSites.forEach(siteData => {
        const siteID = siteData['site-id'];

        //最新データを取り出す (配列の先頭が最新データ)
        const latestData = siteData.states[0];

        if (siteData.states.length === 0) {
          return sitePwData;
        }
        //サイト全体の消費電力量合算
        /**サイト消費電力量*/
        let siteSumPW = 0;
        //空調機の消費電力量は運転データ毎に分割されているので合算する
        const airconPw = latestData['airconditioner']['power-consumption'];
        /** 空調機の消費電力量 */
        const airconSumPw =
          airconPw !== undefined
            ? convNumKw(airconPw['cooling-mode']) +
              convNumKw(airconPw['heating-mode']) +
              convNumKw(airconPw['other-mode'])
            : '';
        if (airconSumPw !== '') {
          siteSumPW += airconSumPw;
        }
        /** Q-tonの消費電力量 */
        const qtonPw =
          latestData.qton['power-consumption'] !== undefined
            ? convNumKw(latestData.qton['power-consumption']['heating-mode'])
            : '';
        if (qtonPw !== '') {
          siteSumPW += qtonPw;
        }
        /** Hyozanの消費電力量 */
        const hyozanPw =
          latestData.hyozan['power-consumption'] !== undefined
            ? convNumKw(latestData.hyozan['power-consumption']['cooling-mode'])
            : '';
        if (hyozanPw !== '') {
          siteSumPW += hyozanPw;
        }
        /** HCCVQの消費電力量 */
        const hccvqPw =
          latestData.hccvq['power-consumption'] !== undefined
            ? convNumKw(latestData.hccvq['power-consumption']['cooling-mode'])
            : '';
        if (hccvqPw !== '') {
          siteSumPW += hccvqPw;
        }
        /** Chillerの消費電力量 */
        const chillerPw =
          latestData.chiller['power-consumption'] !== undefined
            ? convNumKw(
                latestData.chiller['power-consumption']['cooling-mode']
              ) +
              convNumKw(latestData.chiller['power-consumption']['heating-mode'])
            : '';
        if (chillerPw !== '') {
          siteSumPW += chillerPw;
        }

        //prettier-ignore
        sitePwData.push({
          'site-id'       : siteID,
          'airconditioner': String(airconSumPw),
          'qton'          : String(qtonPw),
          'hyozan'        : String(hyozanPw),
          'hccvq'         : String(hccvqPw),
          'chiller'       : String(chillerPw),
          'site'          : String(siteSumPW),
        });
      });

      return sitePwData;
    }

    /**
     * タイムゾーン別サイトIDリスト
     */
    let timezoneList: type.mysite.timezoneList[] = [];

    //タイムゾーン別にサイトIDをまとめる(API問い合わせる処理を減らすため)
    siteProfile.forEach(siteInfoData => {
      const siteId = siteInfoData['site-id'];
      if (siteInfoData['timezone'] !== undefined) {
        const siteTimezone = mySiteLib.escapeStr(siteInfoData['timezone']);

        /* タイムゾーンの00:00を取得 */
        const start = format(
          utcToZonedTime(new Date(), siteTimezone),
          'yyyy-MM-dd 00:00:00'
        );
        const afterTime = zonedTimeToUtc(start, siteTimezone).toISOString();

        //タイムゾーン別サイトIDリストにタイムゾーンがあるか検索し、無ければ新規追加
        if (
          timezoneList.some(element => element.timezone === siteTimezone) ===
          false
        ) {
          timezoneList.push({
            'after-time': afterTime,
            'timezone': siteTimezone,
            'siteIds': [siteId],
          });
        } else {
          //タイムゾーンがあれば、サイトIDを追加する
          timezoneList.forEach(element => {
            if (element['timezone'] === siteTimezone) {
              element['siteIds'].push(siteId);
            }
          });
        }
      }
    });

    /**
     * 消費電力量リクエスト 定期実行用に処理を切り離した
     */
    function reqStatesSitesPw() {
      /**
       * useEffect内で保持しているサイト消費電力量データ
       * viewSitePwDataと中身は同じ
       */
      let localSiteData: type.mysiteProps.siteKWData[] = [];
      timezoneList!.forEach(element => {
        //１日分の消費電力量取得クエリ
        const query: apiIF.reqParameters.getStatesSites = {
          'after-time': element['after-time'],
          'site-id': element['siteIds']?.join(','),
          'fields': 'power-consumption',
          'every-minute': 1440, //60min × 24h  １日分のデータ取得
        };
        getStatesSites(query)
          .then(response => {
            //配列マージ
            localSiteData = [
              ...calcPwStatesData(response['site-states']),
              ...localSiteData,
            ];
            setViewSitePwData(localSiteData);
          })
          .catch(err => {});
      });
    }

    //ページ遷移時初回読み込み
    reqStatesSitesPw();
    //定期実行
    const intervalId = setInterval(() => {
      reqStatesSitesPw();
    }, 60000);
    return function () {
      clearInterval(intervalId);
    };
  }, [siteProfile]);

  return viewSitePwData;
};
